import { memo } from 'react';
import { useTranslation } from 'react-i18next';

// Fase 5.18 (05-remediation.md) — hallazgo real durante el barrido de `static-components`:
// `ActionBar` se declaraba DENTRO del cuerpo de `FlowNode` (se usa 6 veces en el switch de
// categorías). React ve un tipo de componente nuevo en cada render de `FlowNode` y desmonta/
// remonta ese subárbol cada vez — no rompe nada visible hoy (no tiene estado propio), pero es
// trabajo de reconciliación de sobra y el patrón que React Compiler bloquea. Se saca a module
// scope, recibe lo que antes leía por closure como props.
const ActionBar = memo(function ActionBar({ node, onDelete, onEdit, t }) {
    return (
        <div className="op-fn-action-bar" onMouseDown={e => e.stopPropagation()}>
            <button className="op-fn-action-del" title={t('detailPanel.deleteNodeTitle')} aria-label={t('detailPanel.deleteNodeAriaLabel')} onClick={e => { e.stopPropagation(); onDelete(node.id); }}>✕</button>
            <button className="op-fn-action-edit" title={t('detailPanel.editNodeTitle')} aria-label={t('detailPanel.editNodeAriaLabel')} onClick={e => { e.stopPropagation(); onEdit(node); }}>✎</button>
        </div>
    );
});

// Extraído de OpDetailPanel.jsx (02.3-client.md paso b) -- el switch de renderizado por
// categoría de nodo del canvas de flow (`cliente`/`oper`/`trigger`/`documento`/`estructura`/
// `etapa`). Puramente presentacional: el estado del canvas (posiciones, edges, drag-en-curso)
// sigue viviendo en el container, acá solo se reciben callbacks explícitos.
//
// Nota de comportamiento preservado, no "arreglado": el botón ✕ de un nodo normal (`ActionBar`,
// `onDelete`) limpia el nodo Y sus edges; el botón ✕ propio del contenedor `etapa`
// (`onDeleteEtapaContainer`) solo quita el nodo, sin tocar edges -- son dos rutas de borrado
// distintas en el código original, no una inconsistencia introducida acá.
//
// `memo` (paso c) -- el container ya envuelve `onDelete`/`onDeleteEtapaContainer`/`onEdit` en
// `useCallback` para que esto sirva de algo. `flowNodes`/`flowEdges` siguen pasando el array
// completo (se necesitan para `hasIn`/`hasOut`, calculados aquí) -- con eso, cada nodo SÍ vuelve
// a renderizar cuando cualquier otro nodo se mueve (nueva referencia de array), no solo cuando
// cambia el propio. Documentado como límite conocido, no resuelto: los flujos de este dominio
// tienen unos cuantos nodos por operación, no cientos -- no es el mismo orden de magnitud que
// una lista larga (`content-visibility`), así que no se justificó precalcular un mapa de
// conexiones por nodo fuera del loop solo para este paso.
function FlowNode({
    node, flowNodes, flowEdges, flowSearch, dragging, opDocs,
    onStartDrag, onStartResize, onStartConnect, onEndConnect, onDelete, onDeleteEtapaContainer, onEdit,
}) {
    const { t } = useTranslation('client');
    const filt = flowSearch && !node.label.toLowerCase().includes(flowSearch.toLowerCase());
    const cat = node.category;
    const hasIn = flowEdges.some(e => e.to === node.id);
    const hasOut = flowEdges.some(e => e.from === node.id);
    const nodeBaseStyle = { left: node.x, top: node.y, opacity: filt ? 0.25 : 1, cursor: dragging ? 'grabbing' : 'grab', zIndex: dragging ? 90 : 10 };

    if (cat === 'cliente') return (
        <div className="op-fn-person" style={nodeBaseStyle} onMouseDown={e => onStartDrag(e, node.id)}>
            <ActionBar node={node} onDelete={onDelete} onEdit={onEdit} t={t} />
            {node.avatar ? <img src={node.avatar} className="op-fn-avatar" alt={node.label} draggable={false} loading="lazy" /> : <div className="op-fn-avatar op-fn-avatar-fb">{(node.label || '?')[0]}</div>}
            <div className="op-fn-person-label">{node.label}</div>
            <div className={`op-flow-port op-flow-port-in${hasIn ? ' connected' : ''}`} onMouseUp={e => onEndConnect(e, node.id, 'in')} />
            <div className={`op-flow-port op-flow-port-out${hasOut ? ' connected' : ''}`} onMouseDown={e => onStartConnect(e, node.id, 'out')} />
        </div>
    );

    if (cat === 'oper') return (
        <div className="op-fn-oper" style={nodeBaseStyle} onMouseDown={e => onStartDrag(e, node.id)}>
            <ActionBar node={node} onDelete={onDelete} onEdit={onEdit} t={t} />
            <div className={`op-flow-port op-flow-port-top${hasIn ? ' connected' : ''}`} onMouseUp={e => onEndConnect(e, node.id, 'in')} />
            {node.avatar ? <img src={node.avatar} className="op-fn-oper-avatar" alt={node.label} draggable={false} loading="lazy" /> : <div className="op-fn-oper-avatar op-fn-avatar-fb">{(node.label || '?')[0]}</div>}
            <div className="op-fn-person-label">{node.label}</div>
            <div className={`op-flow-port op-flow-port-bot${hasOut ? ' connected' : ''}`} onMouseDown={e => onStartConnect(e, node.id, 'out')} />
        </div>
    );

    if (cat === 'trigger') {
        if (node.sub === 'operacion-activa') {
            return (
                <div
                    className="op-fn-operacion-activa"
                    style={{
                        ...nodeBaseStyle,
                        position: 'absolute',
                        width: 130,
                        height: 56,
                        background: 'rgba(28, 28, 28, 0.97)',
                        border: '1.5px solid rgba(255, 255, 255, 0.15)',
                        borderLeft: '4.5px solid #818cf8',
                        borderRadius: 'var(--radius-md)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                        userSelect: 'none',
                        padding: '0 var(--space-2) 0 var(--space-4)',
                        zIndex: dragging ? 90 : 10,
                    }}
                    onMouseDown={e => onStartDrag(e, node.id)}
                >
                    <ActionBar node={node} onDelete={onDelete} onEdit={onEdit} t={t} />

                    {/* IN Port at the top border (portIndex 0) */}
                    <div
                        className={`op-flow-port${flowEdges.some(e => e.to === node.id && (e.toPortIndex === 0 || !e.toPortIndex)) ? ' connected' : ''}`}
                        style={{
                            position: 'absolute', top: '-5px', left: '12px', width: '28px', height: '7px',
                            background: flowEdges.some(e => e.to === node.id && (e.toPortIndex === 0 || !e.toPortIndex)) ? '#ffffff' : '#1c1c1c',
                            borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                            border: flowEdges.some(e => e.to === node.id && (e.toPortIndex === 0 || !e.toPortIndex)) ? '2px solid #ffffff' : '2px solid rgba(255,255,255,0.5)',
                            boxSizing: 'border-box',
                        }}
                        onMouseUp={e => onEndConnect(e, node.id, 'in', 0)}
                    />
                    {/* IN Port 1 at the left border (portIndex 1) */}
                    <div
                        className={`op-flow-port${flowEdges.some(e => e.to === node.id && e.toPortIndex === 1) ? ' connected' : ''}`}
                        style={{
                            position: 'absolute', top: '10px', left: '-5px', width: '7px', height: '16px',
                            background: flowEdges.some(e => e.to === node.id && e.toPortIndex === 1) ? '#ffffff' : '#1c1c1c',
                            borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                            border: flowEdges.some(e => e.to === node.id && e.toPortIndex === 1) ? '2px solid #ffffff' : '2px solid rgba(255,255,255,0.5)',
                            boxSizing: 'border-box',
                        }}
                        onMouseUp={e => onEndConnect(e, node.id, 'in', 1)}
                    />
                    {/* IN Port 2 at the left border (portIndex 2) */}
                    <div
                        className={`op-flow-port${flowEdges.some(e => e.to === node.id && e.toPortIndex === 2) ? ' connected' : ''}`}
                        style={{
                            position: 'absolute', top: '30px', left: '-5px', width: '7px', height: '16px',
                            background: flowEdges.some(e => e.to === node.id && e.toPortIndex === 2) ? '#ffffff' : '#1c1c1c',
                            borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                            border: flowEdges.some(e => e.to === node.id && e.toPortIndex === 2) ? '2px solid #ffffff' : '2px solid rgba(255,255,255,0.5)',
                            boxSizing: 'border-box',
                        }}
                        onMouseUp={e => onEndConnect(e, node.id, 'in', 2)}
                    />
                    {/* database label below the top IN port */}
                    <div style={{ position: 'absolute', top: '2px', left: '12px', fontSize: '8px', color: 'rgba(255,255,255,0.4)', transform: 'scale(0.85)', transformOrigin: 'top left', pointerEvents: 'none' }}>
                        database
                    </div>

                    {/* Content: Icon and Label */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', marginTop: 'var(--space-1)' }}>
                        <span style={{ fontSize: '14px' }}>⚡</span>
                        <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#fff', whiteSpace: 'nowrap' }}>
                            Operacion Activa
                        </span>
                    </div>

                    {/* 4 OUT circles at the bottom border */}
                    {[
                        { name: 'client', left: 16 },
                        { name: 'oper', left: 48 },
                        { name: 'broker', left: 80 },
                        { name: 'company', left: 112 },
                    ].map((port, idx) => {
                        const isConnected = flowEdges.some(e => e.from === node.id && e.portIndex === idx);
                        return (
                            <div key={port.name} style={{ position: 'absolute', bottom: '-6px', left: port.left - 8, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '16px' }}>
                                <span style={{ position: 'absolute', bottom: '9px', fontSize: '8px', color: 'rgba(255,255,255,0.45)', transform: 'scale(0.8)', whiteSpace: 'nowrap' }}>
                                    {port.name}
                                </span>
                                <div
                                    className={`op-flow-port${isConnected ? ' connected' : ''}`}
                                    style={{ width: '11px', height: '11px', borderRadius: '50%', background: isConnected ? '#ffffff' : '#1c1c1c', border: isConnected ? '2px solid #ffffff' : '2px solid rgba(255,255,255,0.5)', cursor: 'pointer', boxSizing: 'border-box' }}
                                    onMouseDown={e => onStartConnect(e, node.id, 'out', idx)}
                                />
                            </div>
                        );
                    })}
                </div>
            );
        }

        return (
            <div className="op-fn-trigger" style={nodeBaseStyle} onMouseDown={e => onStartDrag(e, node.id)}>
                <ActionBar node={node} onDelete={onDelete} onEdit={onEdit} t={t} />
                <span className="op-fn-trigger-icon">{node.icon}</span>
                <span className="op-fn-trigger-lbl">{node.label}</span>
                <div className={`op-flow-port op-flow-port-in${hasIn ? ' connected' : ''}`} onMouseUp={e => onEndConnect(e, node.id, 'in')} />
                {node.sub === 'ifelse' ? (<>
                    <div className={`op-flow-port op-flow-port-out${hasOut ? ' connected' : ''}`} style={{ top: '30%' }} onMouseDown={e => onStartConnect(e, node.id, 'out', 0)} />
                    <div className={`op-flow-port op-flow-port-out${hasOut ? ' connected' : ''}`} style={{ top: '70%' }} onMouseDown={e => onStartConnect(e, node.id, 'out', 1)} />
                </>) : <div className={`op-flow-port op-flow-port-out${hasOut ? ' connected' : ''}`} onMouseDown={e => onStartConnect(e, node.id, 'out')} />}
            </div>
        );
    }

    if (cat === 'documento') {
        const docData = opDocs[node.sub] || {};
        const nFormData = docData.form_data || {};
        const numDocs = (docData.documents || []).length;
        let pct = 0;

        if (node.sub === 'formulario') {
            const fields = ['nombre_negocio', 'direccion', 'giro', 'representante', 'correo', 'telefono', 'ingreso_mensual', 'ticket_promedio', 'contacto_1_nombre', 'contacto_1_correo', 'contacto_1_tel'];
            let filled = 0;
            fields.forEach(f => { if (nFormData[f]) filled++; });
            if (nFormData.moneda_usd || nFormData.moneda_mxn) filled++;
            filled += numDocs;
            pct = Math.min(100, Math.round(filled / 13) * 100);
        }

        const borderColor = pct === 0 ? 'rgba(255,255,255,0.15)' : (pct >= 100 ? '#10b981' : '#f59e0b');

        return (
            <div className="op-fn-doc" style={{ ...nodeBaseStyle, borderBottom: `2px solid ${borderColor}` }} onMouseDown={e => onStartDrag(e, node.id)}>
                <ActionBar node={node} onDelete={onDelete} onEdit={onEdit} t={t} />
                {nFormData.is_verified ? (
                    <div style={{ position: 'absolute', bottom: '-4px', right: '-4px', width: '10px', height: '10px', background: '#10b981', borderRadius: '50%', border: '2px solid #0d0d0f' }} />
                ) : (
                    nFormData.client_submitted && (
                        <div style={{ position: 'absolute', bottom: '-4px', right: '-4px', width: '10px', height: '10px', background: '#f59e0b', borderRadius: '50%', border: '2px solid #0d0d0f', animation: 'op-pulse-dot 1s infinite alternate' }} />
                    )
                )}
                <span className="op-fn-doc-icon">{node.icon}</span>
                <span className="op-fn-doc-label">{node.label}</span>
                <div className={`op-flow-port op-flow-port-in${hasIn ? ' connected' : ''}`} onMouseUp={e => onEndConnect(e, node.id, 'in')} />
                <div className={`op-flow-port op-flow-port-out${hasOut ? ' connected' : ''}`} onMouseDown={e => onStartConnect(e, node.id, 'out')} />
            </div>
        );
    }

    if (cat === 'estructura') return (
        <div className="op-fn-struct" style={nodeBaseStyle} onMouseDown={e => onStartDrag(e, node.id)}>
            <ActionBar node={node} onDelete={onDelete} onEdit={onEdit} t={t} />
            <div className={`op-flow-port op-flow-port-in${hasIn ? ' connected' : ''}`} onMouseUp={e => onEndConnect(e, node.id, 'in')} />
            <span className="op-fn-struct-icon">{node.icon || '🏢'}</span>
            <span className="op-fn-struct-label">{node.label}</span>
            <div className="op-fn-struct-ports">
                <div className="op-fn-struct-port-wrap" onMouseDown={e => onStartConnect(e, node.id, 'out', 0)}><span>costo</span><div className="op-fn-struct-port-dot" /></div>
                <div className="op-fn-struct-port-wrap" onMouseDown={e => onStartConnect(e, node.id, 'out', 1)}><span>canal</span><div className="op-fn-struct-port-dot" /></div>
                <div className="op-fn-struct-port-wrap" onMouseDown={e => onStartConnect(e, node.id, 'out', 2)}><span>oper</span><div className="op-fn-struct-port-dot" /></div>
            </div>
        </div>
    );

    if (cat === 'etapa') {
        const w = node.w || 260;
        const h = node.h || 180;

        const containedNodes = flowNodes.filter(n2 => {
            if (n2.id === node.id || n2.category === 'etapa') return false;
            return n2.x >= node.x && n2.x <= node.x + w && n2.y >= node.y && n2.y <= node.y + h;
        });
        const numContained = containedNodes.length;

        let colorToken = '#ec4899';
        let borderCol = 'rgba(236, 72, 153, 0.4)';
        let bgCol = 'rgba(236, 72, 153, 0.03)';
        let headerCol = 'rgba(236, 72, 153, 0.15)';

        if (node.sub === 'etapa-negociacion') {
            colorToken = '#eab308'; borderCol = 'rgba(234, 179, 8, 0.4)'; bgCol = 'rgba(234, 179, 8, 0.03)'; headerCol = 'rgba(234, 179, 8, 0.15)';
        } else if (node.sub === 'etapa-espera') {
            colorToken = '#3b82f6'; borderCol = 'rgba(59, 130, 246, 0.4)'; bgCol = 'rgba(59, 130, 246, 0.03)'; headerCol = 'rgba(59, 130, 246, 0.15)';
        } else if (node.sub === 'etapa-cliente') {
            colorToken = '#ec4899'; borderCol = 'rgba(236, 72, 153, 0.4)'; bgCol = 'rgba(236, 72, 153, 0.03)'; headerCol = 'rgba(236, 72, 153, 0.15)';
        } else if (node.sub === 'etapa-renegociacion') {
            colorToken = '#f97316'; borderCol = 'rgba(249, 115, 22, 0.4)'; bgCol = 'rgba(249, 115, 22, 0.03)'; headerCol = 'rgba(249, 115, 22, 0.15)';
        } else if (node.sub === 'etapa-problema') {
            colorToken = '#ef4444'; borderCol = 'rgba(239, 68, 68, 0.4)'; bgCol = 'rgba(239, 68, 68, 0.03)'; headerCol = 'rgba(239, 68, 68, 0.15)';
        } else if (node.sub === 'etapa-cierre') {
            colorToken = '#9ca3af'; borderCol = 'rgba(255, 255, 255, 0.2)'; bgCol = 'rgba(0, 0, 0, 0.55)'; headerCol = 'rgba(0, 0, 0, 0.8)';
        }

        return (
            <div
                className="op-fn-etapa-container"
                style={{
                    position: 'absolute', left: node.x, top: node.y, width: w, height: h,
                    opacity: filt ? 0.25 : 1, cursor: dragging ? 'grabbing' : 'grab', zIndex: dragging ? 90 : 1,
                    border: `1.5px dashed ${borderCol}`, borderRadius: 'var(--radius-md)', background: bgCol, backdropFilter: 'blur(2px)',
                    display: 'flex', flexDirection: 'column', boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                    transition: 'border-color 0.2s, background 0.2s', userSelect: 'none',
                }}
                onMouseDown={e => onStartDrag(e, node.id)}
            >
                <div style={{ background: headerCol, borderBottom: `1px solid ${borderCol}`, padding: 'var(--space-2) var(--space-3)', borderTopLeftRadius: '6.5px', borderTopRightRadius: '6.5px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-2)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                        <span style={{ fontSize: '14px' }}>{node.icon || '📌'}</span>
                        <span style={{ fontSize: '11px', fontWeight: 'bold', color: colorToken, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                            Etapa: {node.label}
                        </span>
                    </div>
                    <button
                        onClick={(e) => { e.stopPropagation(); onDeleteEtapaContainer(node.id); }}
                        aria-label={`Eliminar etapa ${node.label || ''}`}
                        style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: '10px', padding: 0 }}
                    >
                        ✕
                    </button>
                </div>

                <div style={{ flex: 1, position: 'relative', padding: 'var(--space-3)' }}>
                    <div style={{ position: 'absolute', bottom: '8px', left: '12px', fontSize: '9px', color: 'rgba(255,255,255,0.5)', background: 'rgba(0,0,0,0.45)', padding: '2px var(--space-2)', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(255,255,255,0.08)' }}>
                        Contiene: {numContained} {numContained === 1 ? 'elemento' : 'elementos'}
                    </div>
                </div>

                <div
                    style={{ position: 'absolute', bottom: 0, right: 0, width: '15px', height: '15px', cursor: 'nwse-resize', display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', padding: 'var(--space-1)', zIndex: 100 }}
                    onMouseDown={(e) => onStartResize(e, node.id)}
                >
                    <svg width="8" height="8" viewBox="0 0 10 10" fill="none" stroke={colorToken} strokeWidth="1.5">
                        <line x1="0" y1="10" x2="10" y2="0" />
                        <line x1="4" y1="10" x2="10" y2="4" />
                    </svg>
                </div>
            </div>
        );
    }

    return null;
}

export default memo(FlowNode);
